import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import moment from 'moment-timezone';
import { Charge } from '../../../../models/entities/charge';
import { ChargesService } from '../../../../services/charges/charges.service';
import { ModalService } from '../../../../services/modal.service';
import { ScrollService } from '../../../../services/scroll.service';
import { LegfiCurrencyPipe } from '../../../../pipes/format/legfi-currency.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { CategorySelectComponent } from '../../accounting/category-select/category-select.component';
import { SharedModule } from '../../../shared/shared.module';
import { CurrencyInputComponent } from '../../../shared/currency-input/currency-input.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { LegfiDatePipe } from '../../../../pipes/format/legfi-date.pipe';
import Moment = moment.Moment;

/**
 * Converts existing charges into installment payment plans with configurable scheduling.
 *
 * Handles charge balance splitting across multiple installments with remainder distribution
 * and enforces billing-to-due-date buffer requirements (minimum 3 days). Supports
 * automatic late fee configuration that applies to each generated installment.
 */
@UntilDestroy()
@Component({
    selector: 'lf-payment-plan',
    styleUrls: ['./payment-plan.scss'],
    templateUrl: './payment-plan.component.html',
    standalone: true,
    imports: [
        SharedModule,
        CurrencyInputComponent,
        CategorySelectComponent,
        MatDatepickerModule,
        MatMomentDateModule,
        LegfiDatePipe,
        LegfiCurrencyPipe,
    ],
})
export class PaymentPlanComponent implements OnInit
{
    splitBalance: number = 0;
    splitCount: number = 2;
    splitCharges: {
        activeAfter: Moment;
        chargeAmount: number;
        paymentDueOn: Moment;
        minimumDueDate: Moment;
    }[] = [];

    lateFee: {
        fee: number;
        days: number;
        categoryId: number;
    } = {
        fee: 0,
        days: 0,
        categoryId: 0,
    };

    @Input() charges: Charge[] = [];
    @Output() saved: EventEmitter<any> = new EventEmitter();
    @Output() error: EventEmitter<any> = new EventEmitter();

    constructor(
            private _chargesService: ChargesService,
            private _legfiCurrencyPipe: LegfiCurrencyPipe,
            private _modalService: ModalService,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    @ViewChild(CategorySelectComponent) set content(content: CategorySelectComponent) {
        if (content) {
            content.formGroup.get('lateFeeCategory').valueChanges.pipe(untilDestroyed(this)).subscribe({
                next: (value) => {
                    this.lateFee.categoryId = value.id;
                },
            });
        }
    }

    ngOnInit() {
        // Scroll to top on view load.
        this._scrollService.scrollToTop();

        if (this.charges.length < 1) {
            this._modalService.close(PaymentPlanComponent);
            this.error.emit(true);
            // alert('No charges selected to turn into a payment plan.');
            this._growler.oops('No charges selected to turn into a payment plan.');
        }

        const balance = this.charges.map(charge => {
            return charge.balance;
        });
        const splitBalance = balance.reduce((prev, curr) => {
            return prev + curr;
        }, 0);
        this.updateSplitBalance(splitBalance, false);

        const evenPayment = Math.round(splitBalance / this.splitCount);
        const remainder = splitBalance - evenPayment * this.splitCount;
        const splitCharges = [];
        for (let c = 0; c < this.splitCount; c++) {
            splitCharges.push({
                activeAfter: null,
                chargeAmount: c === 0 ? evenPayment : evenPayment + remainder,
                paymentDueOn: null,
                minimumDueDate: null,
            });
        }
        this.splitCharges = splitCharges;
    }

    updateSplitBalance(amount: number, distribute: boolean) {
        if (amount !== this.splitBalance) {
            this.splitBalance = amount;
        }

        if (distribute) {
            // Constrain charges if the balance has decreased.
            let remainder = this.splitBalance;
            for (let c = 0; c < this.splitCharges.length; c++) {
                if (remainder <= this.splitCharges[c].chargeAmount) {
                    this.splitCharges[c].chargeAmount = remainder;
                    break;
                } else {
                    remainder -= this.splitCharges[c].chargeAmount;
                }
            }
        }
    }

    setBiannualSchedule() {
        this.createPaymentSchedule(6, 'month');
    }

    setQuarterlySchedule() {
        this.createPaymentSchedule(3, 'month');
    }

    setMonthlySchedule() {
        this.createPaymentSchedule(1, 'month');
    }

    setYearlySchedule() {
        this.createPaymentSchedule(1, 'year');
    }

    updateSplitCount(splitCount: number) {
        if (splitCount < 1) {
            return;
        }

        if (this.splitCount !== splitCount) {
            this.splitCount = splitCount;
            this.modifyNumberOfSplitCharges();
        }
    }

    /**
     * Redistributes total balance across installments when count changes.
     *
     * Remainder from uneven division is added to the last installment to ensure
     * exact balance reconciliation across all payment plan charges.
     */
    modifyNumberOfSplitCharges() {
        const splitCharges = [];
        let i: number;
        for (i = 0; i < this.splitCharges.length && i < this.splitCount; i++) {
            splitCharges.push(this.splitCharges[i]);
        }
        for (i; i < this.splitCount; i++) {
            splitCharges.push({
                chargeAmount: null,
                activeAfter: null,
                paymentDueOn: null,
                minimumDueDate: null,
            });
        }
        for (i; i < this.splitCharges.length; i++) {
            this.splitCharges.splice(i, 1);
        }

        // Split the amount as evenly as we can
        const evenSplit = Math.floor(this.splitBalance / splitCharges.length);
        // multiply it back out to check for leftover from an odd split
        const mult = evenSplit * splitCharges.length;
        let remainder = 0;
        if (mult < this.splitBalance) {
            remainder = this.splitBalance - mult;
        }

        for (i = 0; i < splitCharges.length; i++) {
            splitCharges[i].chargeAmount = evenSplit;
        }

        // add the previously found leftover to the last invoice
        splitCharges[this.splitCount - 1].chargeAmount += remainder;

        this.splitCharges = splitCharges;
    }

    /**
     * Creates payment plan from validated installment configuration.
     *
     * Enforces billing date must precede due date by 3+ days and validates total
     * installment amounts exactly match original charge balance before API submission.
     */
    splitPayments() {
        const errors: string[] = [];
        if (!(this.splitCharges && this.splitCharges.length)) {
            errors.push(
                    'You must specify at least 2 payments as part of a payment plan.',
            );
        }
        if (this.splitCount !== this.splitCharges.length) {
            errors.push('You must specify ' + this.splitCount + ' new charges');
        }
        this.splitCharges.forEach(charge => {
            if (!(charge.chargeAmount > 0)) {
                errors.push(
                        'Every new payment in the plan must have a charge amount greater than $0.00',
                );
            }
        });

        if (
                this.splitCharges
                        .map(charge => {
                            return charge.chargeAmount;
                        })
                        .reduce((prev: number, curr: number) => {
                            //noinspection TypeScriptUnresolvedVariable
                            return prev + curr;
                        }, 0) !== this.splitBalance
        ) {
            errors.push(
                    'You must specify ' +
                    this._legfiCurrencyPipe.transform(
                            this.splitBalance,
                            'usd',
                            true,
                            '.2',
                    ) +
                    ' worth of payments.',
            );
        }

        for (const charge of this.splitCharges) {
            if (!charge.activeAfter || !charge.paymentDueOn) {
                errors.push(
                        'You must specify a billing and due date for each new charge in the payment plan.',
                );
                break;
            } else if (charge.minimumDueDate > charge.paymentDueOn) {
                errors.push(
                        'The billing date for each new charge must be at least 3 days before the due date.',
                );
                break;
            }
        }

        if (this.lateFee.fee && this.lateFee.categoryId < 1) {
            errors.push('The late fee category must be selected.');
        }

        if (errors.length) {
            this._growler.oops(errors.join('\n'));
            return;
        }

        const body: { [key: string]: any } = {
            chargeIds: this.charges.map(charge => {
                return charge.id;
            }),
            splitTotal: this.splitBalance,
            splitCharges: this.splitCharges.map(split => {
                return {
                    activeAfter: split.activeAfter.format('YYYY-MM-DD'),
                    chargeAmount: split.chargeAmount,
                    paymentDueOn: split.paymentDueOn.format('YYYY-MM-DD'),
                    minimumDueDate: split.minimumDueDate?.format('YYYY-MM-DD'),
                };
            }),
        };
        if (this.lateFee && this.lateFee.fee > 0) {
            body['lateFee'] = this.lateFee;
        }

        this._chargesService.createPaymentPlan(body).subscribe({
            next: (data: any) => {
                this._modalService.save(PaymentPlanComponent, data);
                this.saved.emit(data);
                this._growler.success('Success', 'Payment plan has been created.');
            },
            error: () => {
                // alert('Unable to create payment plan from charge(s).');
                this._growler.oops('Unable to create payment plan from charge(s).');
            },
        });
    }

    // noinspection JSMethodCanBeStatic
    updateActiveAfter(
            split,
            activeAfter: Moment,
    ) {
        if (activeAfter !== null && activeAfter.isValid()) {
            split.paymentDueOn = activeAfter.clone().add(1, 'month');
            if (!split.paymentDueOn || !moment.isMoment(split.paymentDueOn) || !split.paymentDueOn.isValid()) {
                split.minimumDueDate = activeAfter.clone().add(3, 'day');
            }
        }
    }

    private createPaymentSchedule(number, interval) {
        const currentActiveDate = this.splitCharges[0].activeAfter.clone();
        const activeDay = currentActiveDate.date();
        const currentDueDate = this.splitCharges[0].paymentDueOn.clone();
        const dueDay = currentDueDate.date();

        for (let i = 1; i < this.splitCharges.length; i++) {
            currentActiveDate.add(number, interval);
            if (currentActiveDate.daysInMonth() < activeDay) {
                currentActiveDate.date(currentActiveDate.daysInMonth());
            } else {
                currentActiveDate.date(activeDay);
            }

            currentDueDate.add(number, interval);
            if (currentDueDate.daysInMonth() < dueDay) {
                currentDueDate.date(currentDueDate.daysInMonth());
            } else {
                currentDueDate.date(dueDay);
            }

            const split = this.splitCharges[i];
            split.activeAfter = currentActiveDate.clone();
            split.paymentDueOn = currentDueDate.clone();
        }
    }

}
